import { useServices } from '@/app/providers';
import type { MasterNotification, NotificationPriority } from '@yawi/types';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Bell, CheckCheck, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const POLLING_INTERVAL = 30000; // 30 seconds

const getPriorityColor = (priority: NotificationPriority): string => {
  switch (priority) {
    case 'URGENT':
      return 'border-l-red-500 bg-red-50 dark:bg-red-900/20';
    case 'HIGH':
      return 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/20';
    case 'NORMAL':
      return 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20';
    case 'LOW':
    default:
      return 'border-l-gray-300 bg-gray-50 dark:bg-gray-800';
  }
};

const formatTime = (dateString: string): string => {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: es });
  } catch {
    return dateString;
  }
};

interface NotificationItemProps {
  notification: MasterNotification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

const NotificationItem = ({ notification, onMarkAsRead, onDelete }: NotificationItemProps) => {
  const isUnread = !notification.isRead;

  return (
    <div
      className={`p-3 border-l-4 transition-colors cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${getPriorityColor(notification.priority)} ${isUnread ? '' : 'opacity-70'}`}
      onClick={() => isUnread && onMarkAsRead(notification.id)}
    >
      <div className="flex items-start gap-2">
        {isUnread && <div className="w-2 h-2 rounded-full bg-brand-500 mt-1.5 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${isUnread ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} truncate`}>
            {notification.title}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">{notification.body}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{formatTime(notification.createdAt)}</p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          className="p-1 text-gray-400 hover:text-error-500 hover:bg-error-50 dark:hover:bg-error-900/30 rounded transition-colors"
          title="Eliminar notificación"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export function NotificationBell() {
  const { adminNotificationRepository } = useServices();
  const [notifications, setNotifications] = useState<MasterNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const [paginatedResult, count] = await Promise.all([
        adminNotificationRepository.getMyNotifications(1, 10),
        adminNotificationRepository.getUnreadCount(),
      ]);

      if (isMountedRef.current) {
        setNotifications(paginatedResult.data);
        setUnreadCount(count);
      }
    } catch (e) {
      console.error('Error fetching notifications:', e);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [adminNotificationRepository]);

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    fetchNotifications();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchNotifications]);

  // Polling
  useEffect(() => {
    const interval = setInterval(() => {
      adminNotificationRepository.getUnreadCount().then(count => {
        if (isMountedRef.current) {
          setUnreadCount(count);
        }
      }).catch(console.error);
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [adminNotificationRepository]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await adminNotificationRepository.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await adminNotificationRepository.markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (e) {
      console.error('Error marking all notifications as read:', e);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminNotificationRepository.deleteNotification(id);
      const wasUnread = notifications.find(n => n.id === id)?.isRead === false;
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (e) {
      console.error('Error deleting notification:', e);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-10 h-10 text-gray-500 rounded-lg dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-error-500 rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-bold text-gray-800 dark:text-gray-100">Notificaciones</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/30 rounded transition-colors"
                  title="Marcar todas como leídas"
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <div className="animate-spin w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-brand-500 rounded-full mx-auto" />
                <p className="text-sm mt-2">Cargando...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No tienes notificaciones</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
